import { motion } from "framer-motion";
import { Download, ArrowLeft, Mail, Phone, MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT } from "../data/content";
import { ContactButton } from "../components/site/ContactFormDialog";

const ASSETS = [
  {
    key: "logo-tpoz",
    src: "/logo-tpoz.jpg",
    label: "Logo T'POZ complet",
    desc: "Version signature avec baseline « Répit • Ressourcement ».",
  },
  {
    key: "logo-icon",
    src: "/logo-tpoz-icon.jpg",
    label: "Logo T'POZ — icône",
    desc: "Marque abrégée avec la feuille d'anthurium. Idéal réseaux sociaux.",
  },
  {
    key: "logo-pblv",
    src: "/logo-pblv971.jpg",
    label: "Logo Plus Belle La Vie 971",
    desc: "Marque de l'association porteuse du réseau T'POZ.",
  },
  {
    key: "qr",
    src: "/qr-tpoz.png",
    label: "QR Code T'POZ",
    desc: "Redirige vers la carte des lieux. Prêt à imprimer sur affiche/flyer.",
  },
];

const KEY_FIGURES = [
  { value: "40+", label: "Femmes de plus de quarante ans" },
  { value: "3", label: "T'POZ offertes par bénéficiaire et par an" },
  { value: "0", label: "Adhésion requise — réseau solidaire" },
];

const PressAsset = ({ item, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`presse-asset-${item.key}`}
    className="flex flex-col overflow-hidden rounded-3xl border border-pblv-ink/10 bg-white shadow-[0_8px_30px_rgba(45,40,48,0.04)]"
  >
    <div className="flex h-64 items-center justify-center bg-pblv-bg2 p-8">
      <img
        src={item.src}
        alt={item.label}
        className="max-h-full max-w-full object-contain"
        loading="lazy"
      />
    </div>
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div>
        <h3 className="font-serif text-xl font-medium text-pblv-ink">{item.label}</h3>
        <p className="mt-1 text-sm leading-relaxed text-pblv-inksoft">{item.desc}</p>
      </div>
      <a
        href={item.src}
        download
        data-testid={`presse-download-${item.key}`}
        className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark"
      >
        <Download className="h-4 w-4" strokeWidth={1.8} /> Télécharger
      </a>
    </div>
  </motion.div>
);

export default function Presse() {
  return (
    <div className="min-h-screen bg-pblv-bg" data-testid="page-presse">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-pblv-ink/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1300px] items-center justify-between gap-4 px-5 py-4 md:px-8">
          <Link
            to="/"
            data-testid="presse-back-home"
            className="inline-flex items-center gap-2 text-sm font-semibold text-pblv-ink transition-colors hover:text-pblv-mauve"
          >
            <ArrowLeft className="h-4 w-4" /> Retour au site
          </Link>
          <ContactButton
            subject="Demande presse — T'POZ"
            testId="presse-contact-cta"
            className="inline-flex items-center gap-2 rounded-full bg-pblv-ink px-5 py-2.5 text-sm font-semibold text-pblv-bg transition-all hover:bg-pblv-mauve"
          >
            <Mail className="h-4 w-4" /> Contact presse
          </ContactButton>
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-5 py-16 md:px-8 md:py-24">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-pblv-ink/10 bg-white/70 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-pblv-inksoft backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-pblv-mauve" strokeWidth={2} /> Espace presse
          </p>
          <h1 className="font-serif text-5xl font-medium leading-[0.98] tracking-tight text-pblv-ink md:text-7xl">
            Parler de <span className="italic text-pblv-mauve">T'POZ</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-pblv-inksoft md:text-xl">
            Vous êtes journaliste, blogueuse, partenaire média ou institution ?
            Retrouvez ici notre kit visuel officiel, notre pitch, quelques
            chiffres-clés et le contact presse de l'association Plus Belle La
            Vie 971.
          </p>
        </motion.div>

        {/* Pitch */}
        <section className="mt-16 grid gap-10 rounded-3xl border border-pblv-ink/10 bg-white p-8 md:mt-24 md:grid-cols-3 md:p-12">
          <div className="md:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
              Le pitch
            </p>
            <h2 className="mt-2 font-serif text-3xl font-medium leading-[1.1] text-pblv-ink md:text-4xl">
              Un réseau solidaire de <span className="italic">répit et de ressourcement</span> pour les femmes de plus de 40 ans en Guadeloupe.
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-pblv-inksoft md:text-lg">
              <p>
                T'POZ est le réseau imaginé par l'association <b>Plus Belle La Vie 971</b> pour offrir aux femmes traversant une période de vulnérabilité ou de transition de vie (aidance, cancer, séparation, isolement, convalescence, ménopause…) un temps de répit accessible et bienveillant.
              </p>
              <p>
                Le réseau repose sur deux parcours complémentaires : <b>T'POZ Hébergement</b> — des lieux d'accueil pour se poser — et <b>T'POZ Bien-être</b> — des expériences de reconnexion à soi (respiration, gestion du stress, massages, expériences de détente).
              </p>
              <p>
                Coordonné par l'association, il s'appuie sur un réseau territorial d'hébergeurs, praticien·nes du bien-être et partenaires relais engagés à faire du répit une réalité accessible, près de chez soi.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {KEY_FIGURES.map((f, i) => (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl border border-pblv-mauve/20 bg-pblv-mauve/5 p-6"
              >
                <p className="font-serif text-5xl font-semibold text-pblv-mauve">{f.value}</p>
                <p className="mt-1 text-sm font-medium leading-snug text-pblv-inksoft">
                  {f.label}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Kit visuel */}
        <section className="mt-16 md:mt-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pblv-mauve">
              Kit visuel
            </p>
            <h2 className="mt-3 font-serif text-4xl font-medium leading-[1.05] text-pblv-ink md:text-5xl">
              À télécharger,
              <br />
              <span className="italic">à partager.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-pblv-inksoft md:text-lg">
              Logos, QR code et supports visuels officiels — libres d'utilisation pour tout article, reportage ou communication citant T'POZ / Plus Belle La Vie 971.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ASSETS.map((item, i) => (
              <PressAsset key={item.key} item={item} index={i} />
            ))}
          </div>
        </section>

        {/* Contact presse */}
        <section
          data-testid="presse-contact-section"
          className="mt-16 rounded-3xl bg-pblv-ink p-10 text-pblv-bg md:mt-24 md:p-16"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pblv-rose">
            Contact presse
          </p>
          <h2 className="mt-3 font-serif text-4xl font-medium leading-[1.05] text-pblv-bg md:text-5xl">
            Une question, une interview,
            <br />
            <span className="italic text-pblv-rose">un reportage ?</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-pblv-bg/80 md:text-lg">
            La fondatrice et l'équipe de Plus Belle La Vie 971 se tiennent à votre disposition pour toute demande d'information, d'entretien ou de reportage sur le réseau T'POZ.
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <ContactButton
              subject="Demande presse — T'POZ"
              testId="presse-email-link"
              className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 transition-all hover:-translate-y-0.5 hover:bg-white/10"
            >
              <Mail className="mt-1 h-5 w-5 text-pblv-rose" strokeWidth={1.8} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-pblv-bg/60">E-mail</p>
                <p className="mt-1 text-base font-semibold text-pblv-bg">{CONTACT.email}</p>
              </div>
            </ContactButton>
            <a
              href={CONTACT.phoneHref}
              className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 transition-all hover:-translate-y-0.5 hover:bg-white/10"
              data-testid="presse-phone-link"
            >
              <Phone className="mt-1 h-5 w-5 text-pblv-rose" strokeWidth={1.8} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-pblv-bg/60">Téléphone</p>
                <p className="mt-1 text-base font-semibold text-pblv-bg">{CONTACT.phone}</p>
              </div>
            </a>
            <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-5">
              <MapPin className="mt-1 h-5 w-5 text-pblv-rose" strokeWidth={1.8} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-pblv-bg/60">Adresse</p>
                <p className="mt-1 text-base font-semibold text-pblv-bg">{CONTACT.address}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
